import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useReviewDetailsSeen } from '../../context/ReviewDetailsSeenContext';
import { REVIEW_SEEN_DWELL_MS } from '../../utils/reviewDetails/reviewDetailsConstants';

function ReviewSeenDwellObserver({
  reviewType,
  reviewId,
  isUnseen,
  children,
}) {
  const containerRef = useRef(null);
  const dwellTimerRef = useRef(null);
  const intersectingRef = useRef(false);
  const { enqueueSeen } = useReviewDetailsSeen();
  const enqueueSeenRef = useRef(enqueueSeen);

  useEffect(() => {
    enqueueSeenRef.current = enqueueSeen;
  }, [enqueueSeen]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !isUnseen) {
      return undefined;
    }

    const clearDwellTimer = () => {
      if (dwellTimerRef.current !== null) {
        window.clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
    };

    const startDwellTimer = () => {
      clearDwellTimer();
      if (document.visibilityState !== 'visible') {
        return;
      }
      dwellTimerRef.current = window.setTimeout(() => {
        enqueueSeenRef.current(reviewType, reviewId);
      }, REVIEW_SEEN_DWELL_MS);
    };

    const observer = new globalThis.IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        intersectingRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        if (intersectingRef.current) {
          startDwellTimer();
        } else {
          clearDwellTimer();
        }
      },
      { threshold: [0.5] },
    );

    observer.observe(element);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearDwellTimer();
        return;
      }
      if (intersectingRef.current) {
        startDwellTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      observer.disconnect();
      clearDwellTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isUnseen, reviewId, reviewType]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
}

ReviewSeenDwellObserver.propTypes = {
  reviewType: PropTypes.oneOf(['beer', 'hospitality', 'event']).isRequired,
  reviewId: PropTypes.string.isRequired,
  isUnseen: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default ReviewSeenDwellObserver;
